import React, { useEffect, useMemo, useState } from 'react';
import { v3_all_remark_options } from '../../actions/v3/masterData';

const MANUAL_VALUE = '__manual__';

const DEFAULT_REMARK_OPTIONS = [
    'Awaiting yard response',
    'Pending rectification',
    'Rectification completed',
    'Re-inspection required',
    'Accepted after rectification',
    'Observation noted',
    'For information',
    'For action',
    'To be monitored',
    'Closed after verification',
    'Hold point clearance required',
    'Material pending',
    'Document pending',
    'Workmanship issue to be rectified',
    'As per approved drawing',
    'Deviation to be corrected',
];

let cachedRemarkOptions = null;
let remarkOptionsRequest = null;

const normalizeRemarkOptions = (options = []) => {
    const seen = new Set();

    return options
        .map((option) => (
            typeof option === 'string'
                ? option
                : option?.OPTION_NAME || option?.name || option?.label || ''
        ))
        .map((option) => String(option || '').trim())
        .filter(Boolean)
        .filter((option) => {
            const key = option.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

const loadRemarkOptions = async () => {
    if (cachedRemarkOptions) return cachedRemarkOptions;
    if (!remarkOptionsRequest) {
        remarkOptionsRequest = v3_all_remark_options()
            .then((res) => {
                const options = res?.status ? normalizeRemarkOptions(res.result) : [];
                cachedRemarkOptions = options.length ? options : DEFAULT_REMARK_OPTIONS;
                return cachedRemarkOptions;
            })
            .catch(() => {
                cachedRemarkOptions = DEFAULT_REMARK_OPTIONS;
                return cachedRemarkOptions;
            })
            .finally(() => {
                remarkOptionsRequest = null;
            });
    }

    return remarkOptionsRequest;
};

const RemarksSelect = ({
    label = 'Remarks',
    name,
    value = '',
    onChange,
    error = '',
    required = false,
    placeholder = 'Enter remarks',
    wrapperClassName = 'col-md-12 mb-3',
    rows = 2,
}) => {
    const [options, setOptions] = useState(cachedRemarkOptions || DEFAULT_REMARK_OPTIONS);
    const [manualMode, setManualMode] = useState(false);

    useEffect(() => {
        let active = true;
        loadRemarkOptions().then((loadedOptions) => {
            if (active) setOptions(loadedOptions);
        });

        return () => {
            active = false;
        };
    }, []);

    const normalizedValue = String(value || '');
    const hasMatchingOption = useMemo(
        () => options.some((option) => option === normalizedValue),
        [options, normalizedValue]
    );
    const selectValue = manualMode || (normalizedValue && !hasMatchingOption)
        ? MANUAL_VALUE
        : normalizedValue;

    const emitChange = (nextValue) => {
        if (onChange) {
            onChange({
                target: {
                    name,
                    value: nextValue,
                },
            });
        }
    };

    const handleSelectChange = (event) => {
        const nextValue = event.target.value;

        if (nextValue === MANUAL_VALUE) {
            setManualMode(true);
            return;
        }

        setManualMode(false);
        emitChange(nextValue);
    };

    const handleManualChange = (event) => {
        emitChange(event.target.value);
    };

    return (
        <div className={wrapperClassName}>
            {label && (
                <label>
                    {label} {required && <span className="text-danger"> *</span>}
                </label>
            )}
            <select
                name={`${name}Option`}
                value={selectValue}
                onChange={handleSelectChange}
                className={`form-select ${error ? 'is-invalid' : ''}`}
            >
                <option value="">Select {String(label || 'remarks').toLowerCase()}</option>
                {options.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
                <option value={MANUAL_VALUE}>Manual input</option>
            </select>

            {(manualMode || selectValue === MANUAL_VALUE) && (
                <textarea
                    rows={rows}
                    name={name}
                    placeholder={placeholder}
                    value={normalizedValue}
                    onChange={handleManualChange}
                    className={`form-control mt-2 ${error ? 'is-invalid' : ''}`}
                />
            )}
            {error && <div className="invalid-feedback d-block">{error}</div>}
        </div>
    );
};

export default RemarksSelect;
